// What the signed-in user may do, as the browser understands it. A permission is "<area>.<action>",
// such as "sales.create". The server checks every request itself; this only decides which pages,
// menu entries and buttons are shown.

export const hasPermission = (granted: readonly string[] | undefined | null, needed: string): boolean => {
  if (!granted || granted.length === 0) return false;
  if (granted.includes('*') || granted.includes(needed)) return true;
  return granted.includes(`${needed.split('.')[0]}.*`);
};

// The permission each page needs to be opened. Pages that are not listed are open to everyone who is
// signed in (your own profile, settings and notifications). The longest matching path wins, so
// "/master-data/gst-rates" is covered by "/master-data".
export const PAGE_PERMISSIONS: Record<string, string> = {
  '/dashboard': 'analytics.read',
  '/inventory': 'inventory.read',
  '/warehouses': 'inventory.read',
  '/stock-transfers': 'inventory.read',
  '/reservations': 'inventory.read',
  '/batches': 'inventory.read',
  '/serials': 'inventory.read',
  '/stock-valuation': 'inventory.read',
  '/manufacturing': 'manufacturing.read',
  '/routings': 'manufacturing.read',
  '/job-cards': 'manufacturing.read',
  '/oee': 'manufacturing.read',
  '/mrp': 'manufacturing.read',
  '/capacity': 'manufacturing.read',
  '/maintenance': 'manufacturing.read',
  '/tooling': 'manufacturing.read',
  '/purchase': 'purchase.read',
  '/indents': 'purchase.read',
  '/rfqs': 'purchase.read',
  '/goods-receipts': 'purchase.read',
  '/inspections': 'quality.read',
  '/instruments': 'quality.read',
  '/part-approval': 'quality.read',
  '/ncrs': 'quality.read',
  '/scrap': 'inventory.read',
  '/payables': 'purchase.read',
  '/vendors': 'vendors.read',
  '/approved-vendors': 'vendors.read',
  '/sales': 'sales.read',
  '/dispatches': 'sales.read',
  '/sales-dashboard': 'sales.read',
  '/price-lists': 'sales.read',
  '/enquiries': 'sales.read',
  '/cost-sheets': 'sales.read',
  '/rate-contracts': 'sales.read',
  '/customer-schedules': 'sales.read',
  '/quotes': 'sales.read',
  '/demand-forecasting': 'sales.read',
  '/customers': 'customers.read',
  '/invoices': 'gst.read',
  '/payments': 'gst.read',
  '/hsn-codes': 'gst.read',
  '/gst-reports': 'gst.read',
  '/analytics': 'analytics.read',
  '/mis': 'mis.read',
  '/reports': 'reports.read',
  '/master-data': 'master_data.read',
  '/users': 'users.read',
  '/roles': 'roles.read',
  '/company-settings': 'settings.read',
  '/audit-log': 'audit.read',
  '/integrations': 'integrations.read',
  '/documents': 'documents.read',
  '/shop-floor': 'manufacturing.read',
  '/hr': 'hr.read',
  '/backup': 'backup.read',
};

export const permissionForPage = (path: string): string | null => {
  const match = Object.keys(PAGE_PERMISSIONS)
    .filter((prefix) => path === prefix || path.startsWith(`${prefix}/`))
    .sort((a, b) => b.length - a.length)[0];
  return match ? PAGE_PERMISSIONS[match] : null;
};

export const canOpenPage = (granted: readonly string[] | undefined | null, path: string): boolean => {
  const needed = permissionForPage(path);
  return !needed || hasPermission(granted, needed);
};

// Where to send someone who may not open the page they asked for: the first page they may open
const LANDING_ORDER = ['/dashboard', '/sales', '/inventory', '/purchase', '/manufacturing', '/invoices', '/customers', '/vendors', '/reports', '/master-data', '/users'];

export const firstAllowedPage = (granted: readonly string[] | undefined | null, preferred?: string): string => {
  if (preferred && canOpenPage(granted, preferred)) return preferred;
  return LANDING_ORDER.find((path) => canOpenPage(granted, path)) ?? '/profile';
};
